/**
 * One mother, as her doula sees her.
 *
 * Three things she actually does with a client live here — read how the mother
 * has been, talk to her, and record a visit — because they are one piece of
 * work, not three. Splitting them across screens is what makes a doula finish a
 * home visit and log it an hour later from memory, if at all.
 *
 * CONSENT IS NOT AN ERROR STATE. The timeline endpoint answers 200 with
 * `shared: false` when the mother has not granted share_with_doula, and that is
 * deliberate: being assigned to someone is not consent to read her history. So
 * this renders a plain sentence, not an error card and not an empty list that
 * reads like a bug. Messaging and visit logging stay available — they do not
 * depend on that consent, and taking them away would punish the doula for the
 * mother's choice.
 *
 * LOGGING A VISIT OPENS FROM THE MOTHER, NEVER FROM A BLANK FORM. The endpoint
 * takes a member_id and refuses anyone off her caseload; asking her to pick the
 * mother again, having just arrived from that mother's page, is asking her to
 * restate what the system already knows — and it is the one field where a
 * mis-click writes a visit onto the wrong person's record.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowLeft, Check, Lock, MessageSquare, ClipboardList } from 'lucide-react';

interface TimelineEntry {
  kind: string;
  at: string;
  summary: string;
  detail: string | null;
}

interface MemberTimeline {
  member_id: number;
  shared: boolean;
  message: string | null;
  entries: TimelineEntry[];
}

interface DoulaMessage {
  id: string;
  direction: string;
  channel: string;
  at: string;
  content: string | null;
  from_whom: string;
  status: string | null;
}

/** Mirrors the encounter_type values service_encounters documents. `office` is
 *  NOT one of them — a seed used it once and the row rendered fine while naming
 *  a type nothing else in the platform understands. */
const VISIT_TYPES = ['home_visit', 'in_person', 'phone', 'field', 'group', 'async_message'] as const;

/** The two activities the shared eight-visit allowance governs. Anything else
 *  she delivers is logged without a code and priced later; offering her the
 *  whole DHCS catalogue here would be asking a doula to do billing. */
const ACTIVITIES = ['DOULA_PRENATAL', 'DOULA_POSTPARTUM'] as const;

function when(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function at(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function DoulaMemberPage() {
  const { t } = useTranslation();
  const params = useParams();
  const memberId = Number(params.memberId);

  const [timeline, setTimeline] = useState<MemberTimeline | null>(null);
  const [messages, setMessages] = useState<DoulaMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [channel, setChannel] = useState('sms');
  const [sending, setSending] = useState(false);

  const [logOpen, setLogOpen] = useState(false);
  const [visitDate, setVisitDate] = useState(today());
  const [visitType, setVisitType] = useState<string>('home_visit');
  const [minutes, setMinutes] = useState('');
  const [activity, setActivity] = useState<string>('DOULA_PRENATAL');
  const [notes, setNotes] = useState('');
  const [logging, setLogging] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(memberId)) {
      setError(t('doulaMember.badId', 'That is not a valid client.'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Settled independently: a mother who has not shared her history can still
      // be messaged, so one refusal must not blank the other half of the page.
      const [tl, ms] = await Promise.allSettled([
        authFetchJson<MemberTimeline>(`/api/doula/v1/members/${memberId}/timeline`),
        authFetchJson<DoulaMessage[]>(`/api/doula/v1/members/${memberId}/messages`),
      ]);
      if (tl.status === 'fulfilled') setTimeline(tl.value);
      if (ms.status === 'fulfilled') setMessages(ms.value ?? []);
      if (tl.status === 'rejected' && ms.status === 'rejected') {
        throw tl.reason instanceof Error ? tl.reason : new Error(String(tl.reason));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doulaMember.loadFailed', 'We could not load this client.'));
    } finally {
      setLoading(false);
    }
  }, [memberId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async () => {
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    setError(null);
    try {
      await authFetchJson(`/api/doula/v1/members/${memberId}/messages`, {
        method: 'POST',
        jsonBody: { content, channel },
      });
      setDraft('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doulaMember.sendFailed', 'We could not send that message.'));
    } finally {
      setSending(false);
    }
  };

  const logVisit = async () => {
    setLogging(true);
    setError(null);
    try {
      const res = await authFetchJson<{ message?: string }>('/api/doula/v1/encounters', {
        method: 'POST',
        jsonBody: {
          member_id: memberId,
          encounter_date: visitDate,
          encounter_type: visitType,
          duration_minutes: minutes.trim() ? Number(minutes) : null,
          activity_code: activity,
          notes: notes.trim() || null,
        },
      });
      // The server's own sentence, which says the visit still has to clear the
      // benefit rules. Replacing it with "Saved" would promise her it is billed.
      setDone(res?.message || t('doulaMember.visitLogged', 'Recorded.'));
      setLogOpen(false);
      setMinutes('');
      setNotes('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doulaMember.logFailed', 'We could not record that visit.'));
    } finally {
      setLogging(false);
    }
  };

  const ordered = useMemo(
    () => [...messages].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()),
    [messages],
  );

  if (loading) return <PageState variant="loading" />;

  return (
    <PageContainer>
      <Link
        to="/doula/caseload"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('doulaMember.backToCaseload', 'Back to my clients')}
      </Link>

      <PageHeader
        title={t('doulaMember.title', 'Your client')}
        description={t('doulaMember.description', 'How she has been, what you have said to each other, and the visits you have recorded.')}
      />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {done && (
        <div className="mb-4 flex items-start gap-2 rounded-md border p-4 text-sm">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{done}</span>
        </div>
      )}

      {/* ---- log a visit ------------------------------------------------- */}
      <section className="mb-8 rounded-md border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <ClipboardList className="h-4 w-4" />
            {t('doulaMember.visits', 'Visits')}
          </h2>
          {!logOpen && (
            <Button size="sm" onClick={() => setLogOpen(true)}>
              {t('doulaMember.logAVisit', 'Log a visit')}
            </Button>
          )}
        </div>

        {logOpen && (
          <div className="mt-4 space-y-3 border-t pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="visit-date">{t('doulaMember.visitDate', 'When was it?')}</Label>
                <Input
                  id="visit-date"
                  type="date"
                  value={visitDate}
                  max={today()}
                  onChange={(e) => setVisitDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="visit-type">{t('doulaMember.visitType', 'What kind of visit?')}</Label>
                <select
                  id="visit-type"
                  value={visitType}
                  onChange={(e) => setVisitType(e.target.value)}
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                >
                  {VISIT_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {t(`doulaMember.visitTypeOption.${v}`, v.replace(/_/g, ' '))}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="visit-minutes">{t('doulaMember.visitMinutes', 'How long, in minutes?')}</Label>
                <Input
                  id="visit-minutes"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  placeholder={t('doulaMember.optional', 'Optional')}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="visit-activity">{t('doulaMember.visitStage', 'Before or after the birth?')}</Label>
                <select
                  id="visit-activity"
                  value={activity}
                  onChange={(e) => setActivity(e.target.value)}
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                >
                  {ACTIVITIES.map((a) => (
                    <option key={a} value={a}>
                      {t(`doulaMember.activity.${a}`, a)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('doulaMember.visitNotes', 'What happened — optional.')}
            />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={logging || !visitDate} onClick={() => void logVisit()}>
                {t('doulaMember.saveVisit', 'Record visit')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setLogOpen(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* ---- how she has been -------------------------------------------- */}
      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('doulaMember.howSheHasBeen', 'How she has been')}
        </h2>

        {timeline && !timeline.shared ? (
          /* Her choice, stated plainly. Not an error, not an empty list. */
          <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-4 text-sm">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span>
              {timeline.message ||
                t('doulaMember.notShared', 'She has not chosen to share her history. You can still message her and record your visits.')}
            </span>
          </div>
        ) : timeline && timeline.entries.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            {t('doulaMember.nothingYet', 'Nothing recorded yet.')}
          </p>
        ) : (
          <ul className="space-y-3">
            {timeline?.entries.map((e, i) => (
              <li key={`${e.kind}-${e.at}-${i}`} className="rounded-md border p-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{t(`doulaMember.kind.${e.kind}`, e.kind.replace(/_/g, ' '))}</Badge>
                  <span className="text-muted-foreground">{when(e.at)}</span>
                </div>
                <p className="mt-1 font-medium">{e.summary}</p>
                {e.detail && <p className="mt-1 text-muted-foreground">{e.detail}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- messages ----------------------------------------------------- */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <MessageSquare className="h-4 w-4" />
          {t('doulaMember.messages', 'Messages')}
        </h2>

        {ordered.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            {t('doulaMember.noMessages', 'You have not messaged each other yet.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {ordered.map((m) => {
              const mine = m.direction === 'outbound';
              return (
                <li key={m.id} className={mine ? 'flex justify-end' : 'flex justify-start'}>
                  <div
                    className={
                      mine
                        ? 'max-w-[85%] rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground'
                        : 'max-w-[85%] rounded-md border px-3 py-2 text-sm'
                    }
                  >
                    {m.content && <p className="whitespace-pre-wrap">{m.content}</p>}
                    <p className={mine ? 'mt-1 text-xs opacity-80' : 'mt-1 text-xs text-muted-foreground'}>
                      {m.from_whom} · {at(m.at)} · {m.channel}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-4 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder={t('doulaMember.writeTo', 'Write to her…')}
          />
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              aria-label={t('doulaMember.channel', 'How to send it')}
              className="rounded-md border px-3 py-2 text-sm"
            >
              {['sms', 'whatsapp', 'email', 'portal'].map((c) => (
                <option key={c} value={c}>
                  {t(`doulaMember.channelOption.${c}`, c)}
                </option>
              ))}
            </select>
            <Button size="sm" disabled={sending || !draft.trim()} onClick={() => void send()}>
              {t('doulaMember.send', 'Send')}
            </Button>
          </div>
        </div>
      </section>
    </PageContainer>
  );
}
